import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { TreatmentPlanTemplateResponse } from '../../api/generated';
import type { RootState } from '../index';

interface TreatmentPlanTemplatesState {
  templatesById: Record<string, TreatmentPlanTemplateResponse>;
  loading: boolean;
  error: string | null;
}

const initialState: TreatmentPlanTemplatesState = {
  templatesById: {},
  loading: false,
  error: null
};

const treatmentPlanTemplatesSlice = createSlice({
  name: 'treatmentPlanTemplates',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setTemplates: (state, action: PayloadAction<TreatmentPlanTemplateResponse[]>) => {
      const templatesById: Record<string, TreatmentPlanTemplateResponse> = {};
      for (const tpl of action.payload) {
        templatesById[tpl.id] = tpl;
      }
      state.templatesById = templatesById;
      state.loading = false;
      state.error = null;
    },
    upsertTemplate: (state, action: PayloadAction<TreatmentPlanTemplateResponse>) => {
      state.templatesById[action.payload.id] = action.payload;
    },
    removeTemplate: (state, action: PayloadAction<string>) => {
      delete state.templatesById[action.payload];
    },
    clearTemplates: (state) => {
      state.templatesById = {};
      state.loading = false;
      state.error = null;
    }
  }
});

export const {
  setLoading,
  setError,
  setTemplates,
  upsertTemplate,
  removeTemplate,
  clearTemplates
} = treatmentPlanTemplatesSlice.actions;

export const selectTreatmentPlanTemplates = (state: RootState): TreatmentPlanTemplateResponse[] =>
  Object.values(state.treatmentPlanTemplatesSlice.templatesById);

export const selectTreatmentPlanTemplateById =
  (id: string | undefined) =>
  (state: RootState): TreatmentPlanTemplateResponse | undefined =>
    id ? state.treatmentPlanTemplatesSlice.templatesById[id] : undefined;

export const selectTreatmentPlanTemplatesLoading = (state: RootState): boolean =>
  state.treatmentPlanTemplatesSlice.loading;

export const selectTreatmentPlanTemplatesError = (state: RootState): string | null =>
  state.treatmentPlanTemplatesSlice.error;

export default treatmentPlanTemplatesSlice.reducer;
